/**
 * 展会详情页 URL 工具
 * 职责：path 构建、segment 解析、legacy 判定（全站链/301 均走此模块）
 */

/** 详情页 path 前缀，不含域名 */
export const EXHIBITION_DETAIL_PREFIX = '/exhibition'

/** 5 类嵌套子页 path 段名（与 router 注册一致） */
export const EXHIBITION_SUB_PAGES = [
  'ticket',
  'apply',
  'periodical',
  'community',
  'expoinfo',
] as const

/** 子页类型：ticket 门票 / apply 展位 / periodical 会刊 / community 社区 / expoinfo 资讯 */
export type ExhibitionSubPage = (typeof EXHIBITION_SUB_PAGES)[number]

/** 嵌套展会对象 URL 字段（News/Order/PeriodicalOrder 等） */
export type ExhibitionUrlFields = {
  exhibitionId?: number | string
  id?: number | string
  customUrl?: string | null
  customUrlEffectiveTime?: string | null
}

/** 生成详情 URL 时的入参 */
export type ExhibitionUrlInput = {
  exhibitionId: number | string
  customUrl?: string | null // 语义 slug，M3 生效后优先于数字 ID
  customUrlEffectiveTime?: string | null // yyyy-MM-dd HH:mm:ss，未到时间仍用 ID
  locale?: 'zh' | 'en'
}

/** 从 pathname 解析出的详情路由信息 */
export type ParsedDetailPath = {
  segment: string
  segmentKind: 'id' | 'slug'
  subPage?: ExhibitionSubPage
}

/** route.params.segment 的解析结果 */
export type ParsedExhibitionRouteSegment = {
  kind: 'id' | 'slug'
  value: string
}

/**
 * 规范化路由/API 中的展会 slug：trim + decodeURIComponent。
 * 避免浏览器 Location 将 ú 编码为 %C3%BA 后，映射/API 二次编码导致 404。
 */
export function decodeExhibitionSlugSegment(segment: string): string {
  let value = String(segment ?? '').trim()
  if (!value) return value
  if (!value.includes('%')) return value
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * customUrl 是否已生效（当前时间 >= customUrlEffectiveTime）
 * URL改造：未到生效时间仍使用数字 ID 目录 URL；无 env 开关，仅看 API 字段
 */
export function isCustomUrlEffective(
  customUrl: string | null | undefined,
  customUrlEffectiveTime: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!customUrl || !String(customUrl).trim()) return false
  if (!customUrlEffectiveTime || !String(customUrlEffectiveTime).trim()) return false
  const normalized = String(customUrlEffectiveTime).trim().replace(' ', 'T')
  const effectiveAt = new Date(normalized)
  if (Number.isNaN(effectiveAt.getTime())) return false
  return now.getTime() >= effectiveAt.getTime()
}

/** 已生效时返回可用 slug，否则 null（仅 customUrl + customUrlEffectiveTime 判定） */
export function resolveEffectiveCustomUrl(
  input: Pick<ExhibitionUrlInput, 'customUrl' | 'customUrlEffectiveTime'>,
): string | null {
  if (!isCustomUrlEffective(input.customUrl, input.customUrlEffectiveTime)) return null
  return decodeExhibitionSlugSegment(String(input.customUrl).replace(/^\/+|\/+$/g, ''))
}

/** 根据生效时间决定 canonical 用数字 ID 还是语义 slug */
function resolveCanonicalSegment(input: ExhibitionUrlInput): string {
  const effectiveSlug = resolveEffectiveCustomUrl(input)
  if (effectiveSlug) return effectiveSlug
  return String(input.exhibitionId)
}

/** 纯数字展会 ID 正则 */
const NUMERIC_ID_PATTERN = /^\d+$/
/** 合法 segment：非空且不含路径分隔符（支持 Unicode slug） */
const SEGMENT_PATTERN = /^[^/]+$/

/** 旧详情主页：/exhibition/1664.html */
export const LEGACY_DETAIL_HTML_PATTERN = /^\/exhibition\/(\d+)\.html$/i

/** 旧子页 path 正则，key 与子页段名一一对应 */
export const LEGACY_DETAIL_SUBPAGE_PATTERNS: Record<ExhibitionSubPage, RegExp> = {
  ticket: /^\/(?:ticket|exhibition\/ticket)\/(\d+)\.html$/i,
  apply: /^\/exhibition\/apply\/(\d+)\.html$/i,
  periodical: /^\/(?:periodical|exhibition\/periodical)\/(\d+)\.html$/i,
  community: /^\/community\/(\d+)\.html$/i,
  expoinfo: /^\/expoinfo\/(\d+)\.html$/i,
}

/** 新详情主页：/exhibition/1664/ 或 /exhibition/ai-expo/ */
export const NEW_DETAIL_PATH_PATTERN = /^\/exhibition\/([^/]+)\/$/

/** 新嵌套子页：/exhibition/1664/ticket/ 等 */
export const NEW_DETAIL_SUBPAGE_PATH_PATTERN =
  /^\/exhibition\/([^/]+)\/(ticket|apply|periodical|community|expoinfo)\/$/i

/** 缺少尾斜杠的新 path：/exhibition/1664 或 /exhibition/1664/ticket */
export const NEW_DETAIL_NO_TRAILING_SLASH_PATTERN =
  /^\/exhibition\/([^/]+)(?:\/(ticket|apply|periodical|community|expoinfo))?$/i

/** 规范化 pathname：去 query、合并多余斜杠 */
export function normalizeExhibitionDetailPathname(pathname: string): string {
  return (pathname || '').split('?')[0].replace(/\/+/g, '/') || '/'
}

/**
 * 将 pathname 各段 percent-encode，供 HTTP Location 头使用。
 * Node 拒绝非 ASCII 与控制字符；站内 href 仍可用未编码 Unicode slug。
 */
export function encodeExhibitionDetailPathnameForRedirect(pathname: string): string {
  return pathname
    .split('/')
    .map(segment => (segment ? encodeURIComponent(segment) : ''))
    .join('/')
}

/**
 * 生成 canonical path（带尾斜杠，不含域名）
 * 站内链、301 目标、canonical 均调用此函数
 */
export function buildExhibitionDetailPath(
  input: ExhibitionUrlInput,
  subPage?: ExhibitionSubPage,
): string {
  const segment = resolveCanonicalSegment(input)
  const base = `${EXHIBITION_DETAIL_PREFIX}/${segment}`
  if (subPage) {
    return `${base}/${subPage}/`
  }
  return `${base}/`
}

/** thirdUrl 是否为可跳转的外链（http/https）；非 URL 文案（如「购票微信小程序」）返回 false */
export function isExternalTicketUrl(thirdUrl?: string | null): boolean {
  return !!thirdUrl && /^https?:\/\//i.test(String(thirdUrl).trim())
}

/** 门票入口 href：合法外链用 thirdUrl，否则走站内 /exhibition/{id}/ticket/ */
export function resolveExhibitionTicketHref(
  thirdUrl: string | null | undefined,
  input: ExhibitionUrlInput,
): string {
  if (isExternalTicketUrl(thirdUrl)) return thirdUrl!
  return buildExhibitionDetailPath(input, 'ticket')
}

/**
 * 解析 route.params.segment：纯数字 → id，否则 → slug
 */
export function parseExhibitionRouteSegment(segment: string): ParsedExhibitionRouteSegment {
  const value = decodeExhibitionSlugSegment(segment)
  if (NUMERIC_ID_PATTERN.test(value)) {
    return { kind: 'id', value }
  }
  return { kind: 'slug', value }
}

/**
 * 判断是否 legacy 详情/子页 URL（供 middleware 匹配旧链）
 */
export function isLegacyExhibitionDetailPath(pathname: string): boolean {
  const path = normalizeExhibitionDetailPathname(pathname)
  if (LEGACY_DETAIL_HTML_PATTERN.test(path)) return true
  return EXHIBITION_SUB_PAGES.some(sub => LEGACY_DETAIL_SUBPAGE_PATTERNS[sub].test(path))
}

/**
 * 从 pathname 提取 segment 与子页类型（兼容新旧两种 path）
 */
export function parseExhibitionDetailPathname(pathname: string): ParsedDetailPath | null {
  const path = normalizeExhibitionDetailPathname(pathname)

  const legacyDetail = LEGACY_DETAIL_HTML_PATTERN.exec(path)
  if (legacyDetail) {
    const id = legacyDetail[1]!
    return { segment: id, segmentKind: 'id' }
  }

  for (const subPage of EXHIBITION_SUB_PAGES) {
    const m = LEGACY_DETAIL_SUBPAGE_PATTERNS[subPage].exec(path)
    if (m) {
      const id = m[1]!
      return { segment: id, segmentKind: 'id', subPage }
    }
  }

  const newSub = NEW_DETAIL_SUBPAGE_PATH_PATTERN.exec(path)
  if (newSub) {
    const segment = decodeExhibitionSlugSegment(newSub[1]!)
    const subPage = newSub[2]!.toLowerCase() as ExhibitionSubPage
    const { kind } = parseExhibitionRouteSegment(segment)
    return { segment, segmentKind: kind, subPage }
  }

  const newDetail = NEW_DETAIL_PATH_PATTERN.exec(path)
  if (newDetail) {
    const segment = decodeExhibitionSlugSegment(newDetail[1]!)
    const { kind } = parseExhibitionRouteSegment(segment)
    return { segment, segmentKind: kind }
  }

  return null
}

/**
 * 新 path 缺少尾斜杠时是否需要 301 补全（如 /exhibition/1664 → /exhibition/1664/）
 */
export function needsExhibitionDetailTrailingSlashRedirect(pathname: string): boolean {
  const path = normalizeExhibitionDetailPathname(pathname)
  // 列表页 /exhibition/ 不做尾斜杠补全
  if (path === '/exhibition' || path === '/exhibition/') return false
  if (isLegacyExhibitionDetailPath(path)) return false
  if (NEW_DETAIL_PATH_PATTERN.test(path) || NEW_DETAIL_SUBPAGE_PATH_PATTERN.test(path)) {
    return false
  }
  const m = NEW_DETAIL_NO_TRAILING_SLASH_PATTERN.exec(path)
  if (!m) return false
  const segment = decodeExhibitionSlugSegment(m[1]!)
  return SEGMENT_PATTERN.test(segment)
}

/** 将无尾斜杠 path 转为带尾斜杠的 canonical path */
export function buildExhibitionDetailTrailingSlashPath(pathname: string): string | null {
  const path = normalizeExhibitionDetailPathname(pathname)
  const m = NEW_DETAIL_NO_TRAILING_SLASH_PATTERN.exec(path)
  if (!m) return null
  const segment = decodeExhibitionSlugSegment(m[1]!)
  const subPage = m[2]?.toLowerCase() as ExhibitionSubPage | undefined
  if (!SEGMENT_PATTERN.test(segment)) return null
  if (subPage && !EXHIBITION_SUB_PAGES.includes(subPage)) return null
  const { kind } = parseExhibitionRouteSegment(segment)
  return buildExhibitionDetailPath(
    { exhibitionId: kind === 'id' ? segment : segment, customUrl: kind === 'slug' ? segment : null },
    subPage,
  )
}

/**
 * 从 legacy pathname 提取数字展会 ID（仅旧 .html 链可解析）
 * middleware 301 前先用此函数取 ID 再调详情 API
 */
export function extractLegacyExhibitionDetailId(pathname: string): {
  exhibitionId: string
  subPage?: ExhibitionSubPage
} | null {
  const parsed = parseExhibitionDetailPathname(pathname)
  if (!parsed || parsed.segmentKind !== 'id') return null
  if (!NUMERIC_ID_PATTERN.test(parsed.segment)) return null
  if (!isLegacyExhibitionDetailPath(pathname)) return null
  return { exhibitionId: parsed.segment, subPage: parsed.subPage }
}

/**
 * 校验展会详情 API 响应是否可展示
 * 404 守卫：不存在或已下架（id=4 为特例）则不可 301
 */
export function isExhibitionDetailApiAvailable(
  response: { code?: unknown; data?: { online?: number; id?: number } | null } | null | undefined,
): boolean {
  if (!response || String(response.code) !== '1') return false
  const data = response.data
  if (!data) return false
  if (data.online !== 1 && data.id !== 4) return false
  return true
}

/** 从详情 API 响应体取数字 exhibitionId */
export function getExhibitionIdFromApiResponse(
  response: { data?: { exhibitionId?: number; id?: number } | null } | null | undefined,
): number | null {
  const data = response?.data
  if (!data) return null
  const id = data.exhibitionId ?? data.id
  return id != null ? Number(id) : null
}

/** 从展会对象提取 URL 入参（列表卡片 / API 响应通用，含生效时间判断） */
export function toExhibitionUrlInputFromApi(
  fair:
    | {
        exhibitionId?: number | string
        id?: number | string
        customUrl?: string | null
        customUrlEffectiveTime?: string | null
        locale?: string | null
      }
    | null
    | undefined,
): ExhibitionUrlInput {
  const exhibitionId = fair?.exhibitionId ?? fair?.id ?? ''
  const customUrl = fair?.customUrl ?? null
  const customUrlEffectiveTime = fair?.customUrlEffectiveTime ?? null
  return {
    exhibitionId,
    customUrl,
    customUrlEffectiveTime,
  }
}

/**
 * slug 解析结果 locale 是否与当前站点 host 匹配
 * 同一 customUrl 可对应不同 locale 展会（如 hotelex-shanghai: EN=12015, CN=1664），禁止跨 locale 复用
 */
export function isExhibitionDetailLocaleCompatibleWithHost(
  fair: { locale?: string | null } | null | undefined,
  host?: string | null,
): boolean {
  const dataLocale = String(fair?.locale || 'CN').toUpperCase()
  if (isEnExhibitionSiteHost(host)) return dataLocale === 'EN'
  return dataLocale !== 'EN'
}

/** 英文站 host 判定（en.jufair.com / pre-pc-en 等） */
export function isEnExhibitionSiteHost(host?: string | null): boolean {
  const h = String(host || '').toLowerCase().split(':')[0]
  return h.startsWith('en.') || h.includes('en.jufair')
}

/**
 * 英文站是否可用语义 slug
 * CN locale 展会在 custom-url?locale=EN 不存在，301/站内链应保留数字 ID
 */
export function shouldUseSemanticSlugForHost(
  fair: { locale?: string | null } | null | undefined,
  host?: string | null,
): boolean {
  if (!isEnExhibitionSiteHost(host)) return true
  const locale = String(fair?.locale || '').toUpperCase()
  if (!locale || locale === 'CN') return false
  return true
}

/**
 * 按请求 host 构造 URL 入参：英文站 + CN 展会强制用数字 ID，避免 301 到 EN 无法解析的 slug
 */
export function toExhibitionUrlInputFromApiForHost(
  fair:
    | {
        exhibitionId?: number | string
        id?: number | string
        customUrl?: string | null
        customUrlEffectiveTime?: string | null
        locale?: string | null
      }
    | null
    | undefined,
  host?: string | null,
): ExhibitionUrlInput {
  const input = toExhibitionUrlInputFromApi(fair)
  if (!shouldUseSemanticSlugForHost(fair, host)) {
    return {
      exhibitionId: input.exhibitionId,
      customUrl: null,
      customUrlEffectiveTime: null,
    }
  }
  return input
}

/** 从展会对象提取 URL 入参（走 toExhibitionUrlInputFromApi，build 时按生效时间选 segment） */
export function toExhibitionUrlInput(
  fair:
    | {
        exhibitionId?: number | string
        id?: number | string
        customUrl?: string | null
        customUrlEffectiveTime?: string | null
      }
    | null
    | undefined,
): ExhibitionUrlInput {
  return toExhibitionUrlInputFromApi(fair)
}

/**
 * ID 目录 segment 是否应 301 到语义 slug
 * @returns 目标 slug，无需跳转则 null
 */
export function resolveSemanticSlugRedirectTarget(
  segment: string,
  apiData: {
    exhibitionId?: number | string
    id?: number | string
    customUrl?: string | null
    customUrlEffectiveTime?: string | null
    locale?: string | null
  } | null | undefined,
  host?: string | null,
): string | null {
  if (parseExhibitionRouteSegment(segment).kind !== 'id') return null
  if (!shouldUseSemanticSlugForHost(apiData, host)) return null
  const input = toExhibitionUrlInputFromApi(apiData)
  const effectiveSlug = resolveEffectiveCustomUrl(input)
  if (!effectiveSlug || effectiveSlug === segment) return null
  return effectiveSlug
}

/** 带 hash 的详情 path（如 #view、#content） */
export function buildExhibitionDetailPathWithHash(
  input: ExhibitionUrlInput,
  hash: string,
  subPage?: ExhibitionSubPage,
): string {
  const path = buildExhibitionDetailPath(input, subPage)
  const normalizedHash = hash.startsWith('#') ? hash : `#${hash}`
  return `${path.replace(/\/$/, '')}${normalizedHash}`
}

/** 带 query 的详情 path（如 apply?type=booth） */
export function buildExhibitionDetailPathWithQuery(
  input: ExhibitionUrlInput,
  query: Record<string, string | number | undefined | null>,
  subPage?: ExhibitionSubPage,
): string {
  const path = buildExhibitionDetailPath(input, subPage)
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value != null && value !== '') qs.set(key, String(value))
  }
  const search = qs.toString()
  return search ? `${path}?${search}` : path
}

/** 绝对 URL（schema / alternate / 跨域跳转） */
export function buildExhibitionDetailAbsoluteUrl(
  input: ExhibitionUrlInput,
  options?: { subPage?: ExhibitionSubPage; origin?: string; hash?: string },
): string {
  let path = buildExhibitionDetailPath(input, options?.subPage)
  if (options?.hash) {
    const h = options.hash.startsWith('#') ? options.hash : `#${options.hash}`
    path = `${path.replace(/\/$/, '')}${h}`
  }
  const origin = (options?.origin || '').replace(/\/$/, '')
  return origin ? `${origin}${path}` : path
}

/**
 * 将单条 legacy 展会详情/子页 path（可含 ?query / #hash）转为新目录式 path
 * 供 FAQ 富文本、内链 API 等 CMS 存量 .html 链归一化
 */
export function rewriteLegacyExhibitionDetailPath(pathAndSuffix: string): string | null {
  if (!pathAndSuffix) return null

  let pathOnly = pathAndSuffix.trim()
  let queryStr = ''
  let hashStr = ''

  const hashIdx = pathOnly.indexOf('#')
  if (hashIdx >= 0) {
    hashStr = pathOnly.slice(hashIdx + 1)
    pathOnly = pathOnly.slice(0, hashIdx)
  }
  const qIdx = pathOnly.indexOf('?')
  if (qIdx >= 0) {
    queryStr = pathOnly.slice(qIdx + 1)
    pathOnly = pathOnly.slice(0, qIdx)
  }

  const normalizedPath = normalizeExhibitionDetailPathname(pathOnly)
  if (!isLegacyExhibitionDetailPath(normalizedPath)) return null

  const parsed = parseExhibitionDetailPathname(normalizedPath)
  if (!parsed || parsed.segmentKind !== 'id') return null

  const input = { exhibitionId: parsed.segment }
  const query: Record<string, string> = {}
  if (queryStr) {
    new URLSearchParams(queryStr).forEach((value, key) => {
      query[key] = value
    })
  }

  if (hashStr) {
    return buildExhibitionDetailPathWithHash(input, hashStr, parsed.subPage)
  }
  if (Object.keys(query).length > 0) {
    return buildExhibitionDetailPathWithQuery(input, query, parsed.subPage)
  }
  return buildExhibitionDetailPath(input, parsed.subPage)
}

/** legacy 展会详情/子页 path 正则（相对或绝对 URL 中的 path 段） */
const LEGACY_EXHIBITION_LINK_IN_TEXT_PATTERN =
  /(?:https?:\/\/(?:www|en)\.jufair\.com)?(\/(?:exhibition\/(?:apply\/\d+\.html|(?:ticket|periodical)\/\d+\.html|\d+\.html)|(?:ticket|periodical|community|expoinfo)\/\d+\.html)(?:\?[^"'<>\s#]*)?(?:#[^"'<>\s]*)?)/gi

/**
 * 将 HTML 片段中的 legacy 展会 .html 内链改写为目录式 path
 * URL改造：FAQ/CMS 富文本 answer 仍存旧链，渲染前归一化
 */
export function rewriteLegacyExhibitionDetailLinksInHtml(html: string): string {
  if (!html || typeof html !== 'string') return html

  return html.replace(LEGACY_EXHIBITION_LINK_IN_TEXT_PATTERN, (full, pathPart: string) => {
    const nextPath = rewriteLegacyExhibitionDetailPath(pathPart)
    if (!nextPath) return full
    if (/^https?:\/\//i.test(full)) {
      const origin = full.match(/^https?:\/\/[^/]+/i)?.[0] || ''
      return `${origin}${nextPath}`
    }
    return nextPath
  })
}
